// ============================================================
// LANES - what kind of line a line of the log is (v9.7.0).
//
// The shared log is one stream of everything: attacks, tests and
// dice, the story's own lines (the ladder, time passing, a scene cut,
// a clock filling, the GM's announcements), the vox, and handouts.
// Following it meant reading all of it. Each line now has a LANE, and
// the viewer chooses which lanes they see.
//
// A narrative line's lane is its type, except an `info` line, which is
// the machinery of a fight (reloads, turns, conditions) unless its
// AUTHOR said otherwise with `lane` - the story's lines do. A vox line
// is talk, except a roll (a test), a handout, and the GM's
// announcement (`system`), which is story.
//
// Pure, zero React dependency.
// ============================================================
import type { LogItem } from '../chat/merge';

export const LANES = ['combat', 'tests', 'story', 'vox', 'handouts'] as const;
export type Lane = (typeof LANES)[number];

export const LANE_LABELS: Record<Lane, string> = {
  combat: 'Combat',
  tests: 'Tests',
  story: 'Story',
  vox: 'Vox',
  handouts: 'Handouts',
};

const isLane = (value: unknown): value is Lane => LANES.includes(value as Lane);

const BY_TYPE: Record<string, Lane> = { 'attack-result': 'combat', 'test-result': 'tests', 'dice-roll': 'tests' };
const BY_KIND: Record<string, Lane> = { roll: 'tests', handout: 'handouts', system: 'story' };

/** The lane of one line of the merged log. */
export function laneOf(item: LogItem): Lane {
  if (item.kind === 'chat') return BY_KIND[item.msg.kind] ?? 'vox';
  const said = (item.entry as { lane?: unknown }).lane;
  if (isLane(said)) return said;
  return BY_TYPE[item.entry.type] ?? 'combat';
}

/**
 * The lanes a viewer has switched OFF, from their stored preference. The
 * old Talk filter (`logFilter: 'talk'`, the vox and the dice) reads as its
 * equivalent until the viewer touches a chip.
 */
export function hiddenLanes(prefs: { logLanesOff?: unknown; logFilter?: unknown }): Lane[] {
  if (Array.isArray(prefs.logLanesOff)) return LANES.filter((lane) => (prefs.logLanesOff as unknown[]).includes(lane));
  return prefs.logFilter === 'talk' ? ['combat', 'story', 'handouts'] : [];
}

/** Switch one lane on or off. */
export function toggleLane(hidden: readonly Lane[], lane: Lane): Lane[] {
  return hidden.includes(lane)
    ? hidden.filter((l) => l !== lane)
    : LANES.filter((l) => l === lane || hidden.includes(l));
}

/** The lines of the lanes that are on. The same list when none is off. */
export function inLanes<T extends LogItem>(items: readonly T[], hidden: readonly Lane[]): T[] {
  return hidden.length === 0 ? [...items] : items.filter((item) => !hidden.includes(laneOf(item)));
}
